/**
 * Realtime API Configuration Constants
 * 
 * Centralized configuration for OpenAI Realtime API (WebSocket).
 * 
 * IMPORTANT: This is the SINGLE SOURCE OF TRUTH for these values.
 * Do not hardcode these values anywhere else in the codebase.
 */

// OpenAI Realtime API Model
// Switched to gpt-realtime-1.5.
//
// See docs/realtime-2.1-investigacao.md for why gpt-realtime-2.1 was rejected
// after extensive A/B testing on 2026-07-10 (instruction leakage, fact-dumping,
// sotaque instability, refusal to roleplay). The 2.1 model is not viable for
// our voice roleplay use case as of this date.
//
// AUTHORITATIVE FOR LIVE SESSIONS: this build-time constant is sent to the backend
// on every connection (session/token routes read req.body.model), so THIS value —
// not the backend's OPENAI_REALTIME_MODEL — decides the model real users get. The
// backend default (apps/api/src/config/webrtc.js) is only a fallback for requests
// that omit a model, and it logs loudly when a client model diverges from it. To
// switch the production model, change THIS line and redeploy the frontend/game.
export const REALTIME_MODEL = 'gpt-realtime-1.5';

// OpenAI Realtime transcription model (speech-to-text for user audio turns).
// Used in session.update → audio.input.transcription.model.
// Centralized here so the model id is NOT hardcoded across the codebase.
// Override at deploy time by editing this line (build-time constant, like REALTIME_MODEL).
//
// gpt-transcribe: high-accuracy STT for Realtime sessions, supports keyword hints
// and multiple language hints. See https://developers.openai.com/api/docs/models/gpt-transcribe
export const TRANSCRIPTION_MODEL = 'gpt-transcribe';

// Default voice for AI avatar
export const DEFAULT_VOICE = 'alloy';

// Available voices for OpenAI Realtime API
// Source: OpenAI Platform Audio Prompts (as of Dec 2024)
// Note: These are DIFFERENT from TTS voices (fable, nova, onyx are TTS-only)
export const AVAILABLE_VOICES = [
  'alloy',
  'ash',
  'ballad',
  'cedar',
  'coral',
  'echo',
  'marin',
  'sage',
  'shimmer',
  'verse'
] as const;

// Audio configuration
// Using browser's built-in echo cancellation + RNNoise ML-based noise suppression
export const AUDIO_CONFIG = {
  sampleRate: 48000, // Professional audio quality
  channelCount: 1,
  echoCancellation: true,   // ✅ Browser AEC enabled
  noiseSuppression: true,   // ✅ ENABLED - Let browser filter ambient noise (wind, birds, etc)
  autoGainControl: true,    // ✅ ENABLED - Normalizes volume across devices
  
  // RNNoise configuration (ML-based noise suppression - Unity team's secret weapon)
  useRNNoise: true,         // ✅ Enable RNNoise WASM (~2-5ms latency, huge quality improvement)
} as const;

// Realtime Session configuration
export const REALTIME_CONFIG = {
  maxReconnectAttempts: 3,
  reconnectDelay: 2000, // ms
  statsUpdateInterval: 5000, // ms
} as const;

// 🎯 Text-audio sync: Feature flag for synced text reveal (karaoke effect).
// When enabled, AI text is revealed proportionally to audio playback position
// instead of appearing instantly. Also delays message finalization until audio
// finishes playing. Set to false to revert to instant text reveal (legacy behavior).
export const ENABLE_SYNCED_TEXT_REVEAL = true;

// VAD (Voice Activity Detection) Configuration
// Based on Unity WF-SoftSkills proven techniques for filtering noise before OpenAI
// These values were battle-tested and achieve "perfect transcripts" even in noisy environments
//
// 🔧 2024-11 TUNING: Increased thresholds to reduce false positives from ambient noise
// (dog barking, children, motorcycles, etc.) - user reported zero false negatives,
// so we have room to be more aggressive in filtering noise.
//
// 🔧 2025-02 TUNING: Significantly tightened thresholds for outdoor/hands-free use.
// Previous values were too sensitive - birds chirping, wind, and ambient noise
// triggered the mic constantly. Now requires clear, sustained human speech.
export const VAD_ACTIVITY_LOOKBACK_MS = 500;

// Quanto tempo de silêncio esperamos pelo speech_stopped do semantic_vad antes de fechar o
// turno nós. Conta-se silêncio contínuo — qualquer som voiced põe o contador a zero —, nunca
// tempo desde o início da fala, para que uma fala longa não seja cortada.
// O silêncio acústico real é este valor + VAD_ACTIVITY_LOOKBACK_MS (~500ms de lookback antes
// do contador começar a subir).
// Único valor a afinar neste mecanismo: menos corta pausas de pensamento, mais prolonga o
// tempo preso quando o aviso falha.
export const SPEECH_STOPPED_WATCHDOG_SILENCE_MS = 3000;

export const VAD_CONFIG = {
  // FFT Analysis
  fftSize: 2048,                    // Larger FFT for better frequency resolution (vs 512 for visualization)
  smoothingTimeConstant: 0.3,       // AnalyserNode smoothing
  
  // Speech Band (300-3400 Hz is human voice frequency range)
  speechBandLowFreq: 300,           // Hz - Lower bound of speech
  speechBandHighFreq: 3400,         // Hz - Upper bound of speech
  
  // Adaptive Noise Floor
  // 🔧 2026-03: Replaced static rmsThreshold with adaptive noise floor.
  // The system continuously measures ambient noise when nobody is speaking and
  // requires audio to be significantly above that floor to count as voiced.
  // This fixes false activations in quiet rooms where AGC amplifies mic self-noise
  // to levels that crossed the old static 0.030 threshold.
  noiseFloorInitial: 0.015,         // Start sensitive; adapts UP quickly in noisy environments (was 0.030 — too high, rejected soft speech for ~30s)
  noiseFloorMinimum: 0.010,         // Absolute floor — prevents threshold from reaching zero with digital silence (was 0.008; raised 2026-07-07 to reduce barge-in on ambient noise)
  noiseFloorMaximum: 0.060,         // Cap so effective threshold doesn't reject normal speech in noisy environments
  noiseFloorAdaptationRate: 0.15,   // EMA alpha — converges in ~2-3s instead of 20-30s (was 0.05)
  noiseFloorMarginMultiplier: 2.5,  // RMS must exceed noiseFloor * this to count as voiced
  playbackMarginBoost: 2.0,         // Extra multiplier during avatar playback — effective margin becomes 5.0x (was 1.8; raised 2026-07-07 to curb barge-in while the avatar speaks)
  playbackEnergyMinimum: 0.040,     // During playback, deliberate speech passes (was 0.030; raised 2026-07-07 — the primary lever for barge-in during avatar speech; tune incrementally with real driving tests)
  playbackMinVoicedTimeMs: 300,     // During playback, require 300ms of sustained voiced audio to open gate
  absoluteEnergyMinimum: 0.020,     // RMS below this is NEVER voiced — kills the unstable speechBandRatio-in-silence problem (was 0.015; raised 2026-07-07 to further suppress phantom activations)
  
  // Speech band ratio threshold (still used as second condition alongside adaptive noise floor)
  speechBandRatioThreshold: 0.50,   // Unchanged — useful at moderate+ energy, irrelevant at near-silence thanks to absoluteEnergyMinimum
  
  // Fast-path thresholds (immediate gate open for clear speech — bypasses accumulation)
  fastPathRmsThreshold: 0.055,      // Midpoint between original 0.045 and aggressive 0.07
  fastPathRatioThreshold: 0.65,     // Back to 0.65 — 0.75 was too restrictive
  
  // Early visual signal: fires onSpeechLikely for waveform feedback before gate opens.
  // Fast-path frames (high RMS + ratio) trigger it immediately (0ms accumulation).
  speechLikelyThresholdMs: 60,

  // Gate Logic
  minVoicedTimeMs: 200,             // Midpoint between original 150ms and aggressive 300ms
  closeHysteresisMs: 900,           // Wait 900ms of silence before closing gate
  chunkDurationMs: 20,              // Assume ~20ms per audio chunk for calculations
  
  // Pre-roll Buffer
  preRollBufferMs: 1000,             // Keep 1000ms (1 second) of audio before gate opens
} as const;
